import moment from 'moment-timezone'
import { connect, useSelector } from 'react-redux'
import * as styles from '../../assets/styles/eventOverview'
import InventoryItem from './InventoryItem'
import ContinueButton from './ContinueButton'
import { parseBoolean } from '../../utils/preloadedState'
import { calcTotalFeePrice } from '../../utils/calcCharges'
import { RESERVATION_TYPE, GUESTLIST_TYPE, GUESTLIST_FREE_TYPE, REQUEST_TYPE } from '../../reducers/inventoryReducer'
import { isMultiInventoryRequestEnabled } from '../../selectors/eventSelector'

const { fonts_color_primary } = widgetInit.settings

const EventInventoryList = ({
  deviceSize,
  inventoryData,
  inventoryEdits: inventoryAvailabilities,
  onContinue,
  isPrivate,
  isBookable,
  isEventOpened,
  date,
  languageCode,
  currencyCode,
  tieredEvents,
  isExperienceMode,
  inventoryCart,
  isFeesInPriceDisplayed,
  defaultServiceCharge,
  isMultiInventoryRequestEnabled,
}) => {
  const currencyFormatter = new Intl.NumberFormat(languageCode, { style: 'currency', currency: currencyCode })
  const inventoryDataList = Object.values(inventoryData).sort((inventoryA, inventoryB) => inventoryA.viewOrder - inventoryB.viewOrder)

  let notAvailableMessage
  if (!isEventOpened && inventoryDataList.length === 0) {
    let msg = 'Booking for this event has not been opened yet, please check back at a later date.'
    if (date) {
      if (date.indexOf('T') !== -1) {
        const openDate = moment(date).format('MMMM D, YYYY')
        msg = `Booking for this event opens on ${openDate}.`
      }
    }
    notAvailableMessage = msg
  } else if (inventoryDataList.length === 0 || (!isExperienceMode && isPrivate && !isBookable)) {
    notAvailableMessage = 'This event is sold out.'
  }

  const shouldDisplayInventory = inventoryItem => {
    if (Array.isArray(tieredEvents) && tieredEvents.indexOf(inventoryItem.inventoryId) >= 0) {
      return false
    }

    const { inventoryId, inventoryType } = inventoryItem
    const inventoryAvailability = inventoryAvailabilities && inventoryAvailabilities[inventoryId]
    const remainingQuantity = Math.min(
      inventoryItem.maxQuantity,
      inventoryAvailability ? inventoryAvailability.remainingQuantity : inventoryItem.maxQuantity
    )

    if (remainingQuantity <= 0 && inventoryType !== REQUEST_TYPE) {
      return false
    }

    return true
  }

  const inventoryTypes = new Set()
  inventoryDataList.forEach(inventory => {
    if (shouldDisplayInventory(inventory)) {
      if (inventory.inventoryType === GUESTLIST_TYPE || inventory.inventoryType === GUESTLIST_FREE_TYPE) {
        inventoryTypes.add(GUESTLIST_TYPE)
      } else {
        inventoryTypes.add(inventory.inventoryType)
      }
    }
  })
  const inventoryTypeDisplayOrder = Array.from(inventoryTypes)
  const hasAtLeastOneItemInCart = inventoryCart.filter(inventoryCartItem => inventoryCartItem.quantity > 0).length > 0

  const renderReservationOrGuestlistTypeInventory = isReservationType => {
    const reservationTypeInventoryData = inventoryDataList.filter(inventory => {
      if (isReservationType) {
        return inventory.inventoryType === RESERVATION_TYPE && shouldDisplayInventory(inventory)
      }

      return (
        (inventory.inventoryType === GUESTLIST_TYPE || inventory.inventoryType === GUESTLIST_FREE_TYPE) && shouldDisplayInventory(inventory)
      )
    })

    const filteredInventoryCart = inventoryCart.filter(
      inventoryCartItem => !!reservationTypeInventoryData.find(inventory => inventory.inventoryId === inventoryCartItem.inventoryId)
    )

    const totalPrice = filteredInventoryCart.reduce((total, inventoryCartItem) => {
      const reservationTypeInventory = reservationTypeInventoryData.find(
        inventory => inventory.inventoryId === inventoryCartItem.inventoryId
      )
      const totalFeePrice = calcTotalFeePrice(reservationTypeInventory, 1, reservationTypeInventory.price, defaultServiceCharge)
      const totalPrice = isFeesInPriceDisplayed ? reservationTypeInventory.price + totalFeePrice : reservationTypeInventory.price
      return total + inventoryCartItem.quantity * totalPrice
    }, 0)

    const handleContinue = () => {
      const inventories = filteredInventoryCart.map(inventoryCartItem => ({
        inventoryId: inventoryCartItem.inventoryId,
        availabilityId: inventoryAvailabilities[inventoryCartItem.inventoryId].availabilityId,
      }))

      onContinue(inventories)
    }

    const hasAtLeastOneItemOfThisTypeInCart = filteredInventoryCart.filter(inventoryCartItem => inventoryCartItem.quantity > 0).length > 0
    const isDisabled = !hasAtLeastOneItemOfThisTypeInCart && hasAtLeastOneItemInCart
    const opacity = isDisabled ? '0.3' : '1'
    const isSmallResolutionDevice = deviceSize === 'small'

    if (reservationTypeInventoryData.length === 0) {
      return null
    }

    return (
      <section key={isReservationType ? RESERVATION_TYPE : GUESTLIST_TYPE} style={{ backgroundColor: styles.rgbaLineBackground }}>
        <>
          <hr style={styles.inventoryItemSeperator} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 400, margin: 0, padding: '16px' }}>
              {isReservationType ? 'Reservations' : 'Guestlist'}
            </h2>
            {isDisabled && (
              <div style={{ fontSize: '16px', marginRight: '16px', textAlign: 'right' }}>Deselect inventory from other categories</div>
            )}
          </div>
          <hr style={styles.inventoryItemSeperator} />
        </>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {reservationTypeInventoryData.map(inventoryItem => (
            <li key={inventoryItem.id} style={{ borderBottom: `solid 1px ${styles.rgbaLineBackground}` }}>
              <InventoryItem
                inventoryItem={inventoryItem}
                inventoryAvailabilities={inventoryAvailabilities}
                currencyCode={currencyCode}
                isDisabled={isDisabled}
                deviceSize={deviceSize}
              />
            </li>
          ))}
        </ul>
        <div
          style={{
            backgroundColor: isDisabled ? styles.rgbaWidgetBackground : styles.rgbaCellBackground,
            padding: '20px 16px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div style={{ color: fonts_color_primary, fontSize: '18px', fontWeight: 600, opacity }}>Total Price</div>
          <div
            style={{
              color: fonts_color_primary,
              fontSize: '16px',
              fontWeight: 600,
              flex: '1 1 auto',
              textAlign: 'right',
              marginRight: isSmallResolutionDevice ? '0' : '18px',
              opacity,
            }}
          >
            {currencyFormatter.format(totalPrice)}
          </div>
          {!isSmallResolutionDevice && (
            <ContinueButton aria-label="continue to checkout" disabled={!hasAtLeastOneItemOfThisTypeInCart} onClick={handleContinue}>
              Continue
            </ContinueButton>
          )}
        </div>
        {isSmallResolutionDevice && (
          <div
            style={{
              backgroundColor: isDisabled ? styles.rgbaWidgetBackground : styles.rgbaCellBackground,
              padding: '0 16px 20px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <ContinueButton aria-label="continue to checkout" disabled={!hasAtLeastOneItemOfThisTypeInCart} onClick={handleContinue}>
              Continue
            </ContinueButton>
          </div>
        )}
      </section>
    )
  }

  const renderRequestInventory = () => {
    const isDisabled = hasAtLeastOneItemInCart
    const requestInventoryData = inventoryDataList.filter(
      inventory => inventory.inventoryType === REQUEST_TYPE && shouldDisplayInventory(inventory)
    )

    if (requestInventoryData.length === 0) {
      return null
    }

    return (
      <section key={REQUEST_TYPE} style={{ backgroundColor: styles.rgbaLineBackground }}>
        <>
          <hr style={styles.inventoryItemSeperator} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 400, margin: 0, padding: '16px' }}>Requests</h2>
            {isDisabled && (
              <div style={{ fontSize: '16px', marginRight: '16px', textAlign: 'right' }}>Deselect inventory from other categories</div>
            )}
          </div>

          <hr style={styles.inventoryItemSeperator} />
        </>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {requestInventoryData.map(inventoryItem => (
            <li key={inventoryItem.id} style={{ borderBottom: `solid 1px ${styles.rgbaLineBackground}` }}>
              <InventoryItem
                inventoryItem={inventoryItem}
                inventoryAvailabilities={inventoryAvailabilities}
                currencyCode={currencyCode}
                isDisabled={isDisabled}
              />
            </li>
          ))}
        </ul>
      </section>
    )
  }

  if (notAvailableMessage) {
    return (
      <div style={styles.inventoryWrapper}>
        <div
          style={{
            ...styles.inventoryListItem,
            padding: '12px 1.5% 13px 5.44285%',
          }}
        >
          <div style={styles.noAvailText}>{notAvailableMessage}</div>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.inventoryWrapper}>
      <div>
        {inventoryTypeDisplayOrder.map(inventoryType => {
          if (!isMultiInventoryRequestEnabled) {
            if (inventoryType === RESERVATION_TYPE) {
              return renderReservationOrGuestlistTypeInventory(true)
            } else if (inventoryType === GUESTLIST_TYPE) {
              return renderReservationOrGuestlistTypeInventory(false)
            } else if (inventoryType === REQUEST_TYPE) {
              return renderRequestInventory()
            }
            return null
          }

          const reservationTypeInventoryData = inventoryDataList.filter(inventory => {
            if (!shouldDisplayInventory(inventory)) {
              return false
            }

            if (inventoryType === RESERVATION_TYPE || inventoryType === REQUEST_TYPE) {
              return inventory.inventoryType === inventoryType
            }

            return inventory.inventoryType === GUESTLIST_TYPE || inventory.inventoryType === GUESTLIST_FREE_TYPE
          })

          const filteredInventoryCart = inventoryCart.filter(
            inventoryCartItem => !!reservationTypeInventoryData.find(inventory => inventory.inventoryId === inventoryCartItem.inventoryId)
          )

          const totalPrice = filteredInventoryCart.reduce((total, inventoryCartItem) => {
            const reservationTypeInventory = reservationTypeInventoryData.find(
              inventory => inventory.inventoryId === inventoryCartItem.inventoryId
            )
            const totalFeePrice = calcTotalFeePrice(reservationTypeInventory, 1, reservationTypeInventory.price, defaultServiceCharge)
            const totalPrice = isFeesInPriceDisplayed ? reservationTypeInventory.price + totalFeePrice : reservationTypeInventory.price
            return total + inventoryCartItem.quantity * totalPrice
          }, 0)

          const handleContinue = () => {
            const inventories = filteredInventoryCart.map(inventoryCartItem => ({
              inventoryId: inventoryCartItem.inventoryId,
              availabilityId: inventoryAvailabilities[inventoryCartItem.inventoryId].availabilityId,
            }))

            onContinue(inventories)
          }

          const hasAtLeastOneItemOfThisTypeInCart =
            filteredInventoryCart.filter(inventoryCartItem => inventoryCartItem.quantity > 0).length > 0
          const isDisabled = !hasAtLeastOneItemOfThisTypeInCart && hasAtLeastOneItemInCart
          const opacity = isDisabled ? '0.3' : '1'
          const isSmallResolutionDevice = deviceSize === 'small'

          if (reservationTypeInventoryData.length === 0) {
            return null
          }

          let headerTitle = 'Guestlist'
          if (inventoryType === RESERVATION_TYPE) {
            headerTitle = 'Reservations'
          } else if (inventoryType === REQUEST_TYPE) {
            headerTitle = 'Requests'
          }

          return (
            <section key={inventoryType} style={{ backgroundColor: styles.rgbaLineBackground }}>
              <>
                <hr style={styles.inventoryItemSeperator} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 400, margin: 0, padding: '16px' }}>{headerTitle}</h2>
                  {isDisabled && (
                    <div style={{ fontSize: '16px', marginRight: '16px', textAlign: 'right' }}>
                      Deselect inventory from other categories
                    </div>
                  )}
                </div>
                <hr style={styles.inventoryItemSeperator} />
              </>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {reservationTypeInventoryData.map(inventoryItem => (
                  <li key={inventoryItem.id} style={{ borderBottom: `solid 1px ${styles.rgbaLineBackground}` }}>
                    <InventoryItem
                      inventoryItem={inventoryItem}
                      inventoryAvailabilities={inventoryAvailabilities}
                      currencyCode={currencyCode}
                      isDisabled={isDisabled}
                      deviceSize={deviceSize}
                    />
                  </li>
                ))}
              </ul>
              <div
                style={{
                  backgroundColor: isDisabled ? styles.rgbaWidgetBackground : styles.rgbaCellBackground,
                  padding: '20px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                {inventoryType !== REQUEST_TYPE &&
                  !reservationTypeInventoryData.every(inventory => inventory.inventoryType === GUESTLIST_FREE_TYPE) && (
                    <>
                      <div style={{ color: fonts_color_primary, fontSize: '18px', fontWeight: 600, opacity }}>Total Price</div>
                      <div
                        style={{
                          color: fonts_color_primary,
                          fontSize: '16px',
                          fontWeight: 600,
                          flex: '1 1 auto',
                          textAlign: 'right',
                          marginRight: isSmallResolutionDevice ? '0' : '18px',
                          opacity,
                        }}
                      >
                        {currencyFormatter.format(totalPrice)}
                      </div>
                    </>
                  )}
                {!isSmallResolutionDevice && (
                  <ContinueButton aria-label="continue to checkout" disabled={!hasAtLeastOneItemOfThisTypeInCart} onClick={handleContinue}>
                    Continue
                  </ContinueButton>
                )}
              </div>
              {isSmallResolutionDevice && (
                <div
                  style={{
                    backgroundColor: isDisabled ? styles.rgbaWidgetBackground : styles.rgbaCellBackground,
                    padding: '0 16px 20px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <ContinueButton aria-label="continue to checkout" disabled={!hasAtLeastOneItemOfThisTypeInCart} onClick={handleContinue}>
                    Continue
                  </ContinueButton>
                </div>
              )}
            </section>
          )
        })}
      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  languageCode: state.app.language,
  inventoryCart: state.inventoryCart.cart,
  isFeesInPriceDisplayed: parseBoolean(state.widgetSettings.isFeesInPriceDisplayed),
  defaultServiceCharge: state.venueInfo.venueDefaultServiceCharge,
  isMultiInventoryRequestEnabled: isMultiInventoryRequestEnabled(state),
})
export default connect(mapStateToProps)(EventInventoryList)
